import { ClickbaitAnalysis } from '../types';

type ClickbaitCategory = 'Curiosity Gap' | 'Vague Drama' | 'Info Withheld' | 'Emotional' | 'Superlatives' | 'Fear Bait' | 'Teaser' | 'Metaphor';

interface PatternDef {
  pattern: RegExp;
  weight: number;
  category: ClickbaitCategory;
}

export const analyzeClickbaitHeuristic = (title: string): ClickbaitAnalysis => {
  let clickbaitScore = 0;
  let cleanedTitle = title;
  let matchedCategory: ClickbaitCategory | undefined;
  let matchedWeight = 0;

  // Strong clickbait indicators (high weight) - English
  const strongPatternsEN: PatternDef[] = [
    { pattern: /you won't believe/i, weight: 3, category: 'Teaser' },
    { pattern: /shocking/i, weight: 2, category: 'Emotional' },
    { pattern: /this one trick/i, weight: 3, category: 'Teaser' },
    { pattern: /doctors hate/i, weight: 3, category: 'Teaser' },
    { pattern: /what happened next/i, weight: 3, category: 'Curiosity Gap' },
    { pattern: /will shock you/i, weight: 3, category: 'Emotional' },
    { pattern: /mind[- ]?blow/i, weight: 2, category: 'Emotional' },
    { pattern: /gone wrong/i, weight: 2, category: 'Fear Bait' },
    { pattern: /gone sexual/i, weight: 3, category: 'Emotional' },
    { pattern: /not clickbait/i, weight: 3, category: 'Teaser' },
    { pattern: /\(not what you think\)/i, weight: 2, category: 'Curiosity Gap' },
  ];

  // Strong clickbait indicators - Polish
  const strongPatternsPL: PatternDef[] = [
    { pattern: /szokuj[aą]c[eya]/i, weight: 2, category: 'Emotional' },
    { pattern: /nie uwierzy(sz|cie)/i, weight: 3, category: 'Teaser' },
    { pattern: /co (było|jest) w środku/i, weight: 3, category: 'Curiosity Gap' },
    { pattern: /co na to/i, weight: 2, category: 'Curiosity Gap' },
    { pattern: /tak zareagował/i, weight: 2, category: 'Curiosity Gap' },
    { pattern: /oto (co|jak|dlaczego)/i, weight: 2, category: 'Curiosity Gap' },
    { pattern: /wiemy,? (co|jak|kto|gdzie)/i, weight: 2, category: 'Curiosity Gap' },
    { pattern: /to koniec/i, weight: 2, category: 'Vague Drama' },
    { pattern: /przelać czarę/i, weight: 2, category: 'Metaphor' },
    { pattern: /dom z kart/i, weight: 2, category: 'Metaphor' },
  ];

  // Moderate clickbait indicators - English
  const moderatePatternsEN: PatternDef[] = [
    { pattern: /^\d+\s+(things?|reasons?|ways?|secrets?|facts?|tips?)/i, weight: 1, category: 'Teaser' },
    { pattern: /the truth about/i, weight: 1, category: 'Curiosity Gap' },
    { pattern: /what nobody tells you/i, weight: 2, category: 'Curiosity Gap' },
    { pattern: /here'?s why/i, weight: 1, category: 'Curiosity Gap' },
    { pattern: /this is why/i, weight: 1, category: 'Curiosity Gap' },
    { pattern: /the reason why/i, weight: 1, category: 'Curiosity Gap' },
    { pattern: /everyone is talking about/i, weight: 2, category: 'Vague Drama' },
    { pattern: /you need to know/i, weight: 1, category: 'Teaser' },
    { pattern: /must see/i, weight: 1, category: 'Emotional' },
    { pattern: /can't miss/i, weight: 1, category: 'Emotional' },
    { pattern: /won't guess/i, weight: 2, category: 'Curiosity Gap' },
    { pattern: /finally revealed/i, weight: 2, category: 'Curiosity Gap' },
    { pattern: /exposed/i, weight: 1, category: 'Vague Drama' },
    { pattern: /insane/i, weight: 1, category: 'Emotional' },
    { pattern: /unbelievable/i, weight: 2, category: 'Emotional' },
    { pattern: /incredible/i, weight: 1, category: 'Superlatives' },
    { pattern: /amazing/i, weight: 1, category: 'Emotional' },
    { pattern: /jaw[- ]?dropping/i, weight: 2, category: 'Emotional' },
  ];

  // Moderate clickbait indicators - Polish
  const moderatePatternsPL: PatternDef[] = [
    { pattern: /niewiarygodne/i, weight: 2, category: 'Emotional' },
    { pattern: /sensacja/i, weight: 2, category: 'Emotional' },
    { pattern: /dramat/i, weight: 1, category: 'Emotional' },
    { pattern: /tragedia/i, weight: 1, category: 'Emotional' },
    { pattern: /słabszy niż kiedykolwiek/i, weight: 2, category: 'Vague Drama' },
    { pattern: /najgorszy w historii/i, weight: 2, category: 'Superlatives' },
    { pattern: /najlepszy w historii/i, weight: 2, category: 'Superlatives' },
    { pattern: /największy w historii/i, weight: 2, category: 'Superlatives' },
    { pattern: /wszyscy mówią o/i, weight: 2, category: 'Vague Drama' },
    { pattern: /musisz (to )?zobaczyć/i, weight: 1, category: 'Teaser' },
    { pattern: /prawda o/i, weight: 1, category: 'Curiosity Gap' },
    { pattern: /dlaczego tak/i, weight: 1, category: 'Curiosity Gap' },
    { pattern: /oto powód/i, weight: 1, category: 'Curiosity Gap' },
    { pattern: /zaskakując[eya]/i, weight: 1, category: 'Emotional' },
    { pattern: /kontrowersyjn[eya]/i, weight: 1, category: 'Emotional' },
    { pattern: /wstrząsając[eya]/i, weight: 2, category: 'Emotional' },
    { pattern: /bulwersując[eya]/i, weight: 2, category: 'Emotional' },
  ];

  // Weak indicators (often clickbait in combination)
  const weakPatterns: PatternDef[] = [
    { pattern: /!{2,}/i, weight: 1, category: 'Emotional' },
    { pattern: /\?!|\?{2,}/i, weight: 1, category: 'Emotional' },
    { pattern: /\.{3,}/i, weight: 1, category: 'Curiosity Gap' },
    { pattern: /^(watch|see|look):/i, weight: 1, category: 'Teaser' },
    { pattern: /ever$/i, weight: 0.5, category: 'Superlatives' },
    { pattern: /\ball caps words\b/i, weight: 0.5, category: 'Emotional' },
  ];

  const strongPatterns = [...strongPatternsEN, ...strongPatternsPL];
  const moderatePatterns = [...moderatePatternsEN, ...moderatePatternsPL];

  // Check for ALL CAPS words (more than 2)
  const capsWords = title.match(/\b[A-Z]{2,}\b/g) || [];
  if (capsWords.length >= 2) {
    clickbaitScore += 1;
  }

  // Apply patterns
  for (const { pattern, weight, category } of [...strongPatterns, ...moderatePatterns, ...weakPatterns]) {
    if (pattern.test(title)) {
      clickbaitScore += weight;
      // Track the highest-weight matched category
      if (weight > matchedWeight) {
        matchedWeight = weight;
        matchedCategory = category;
      }
    }
  }

  // Vague pronoun at start without context (English)
  if (/^(this|these|that|those|he|she|they|it)\s/i.test(title) && title.length < 60) {
    clickbaitScore += 1;
    if (!matchedCategory || matchedWeight < 1) {
      matchedCategory = 'Info Withheld';
      matchedWeight = 1;
    }
  }

  // Vague pronoun at start without context (Polish)
  if (/^(to|ten|ta|ci|te|on|ona|oni)\s/i.test(title) && title.length < 60) {
    clickbaitScore += 1;
    if (!matchedCategory || matchedWeight < 1) {
      matchedCategory = 'Info Withheld';
      matchedWeight = 1;
    }
  }

  // Questions designed to create curiosity gaps (English)
  if (/^(what|why|how|when|where|who)\s.*\?$/i.test(title)) {
    if (title.split(' ').length < 8) {
      clickbaitScore += 0.5;
      if (!matchedCategory) {
        matchedCategory = 'Curiosity Gap';
        matchedWeight = 0.5;
      }
    }
  }

  // Questions designed to create curiosity gaps (Polish)
  if (/^(co|jak|dlaczego|kiedy|gdzie|kto|czy)\s.*\?$/i.test(title)) {
    if (title.split(' ').length < 10) {
      clickbaitScore += 1;
      if (!matchedCategory || matchedWeight < 1) {
        matchedCategory = 'Curiosity Gap';
        matchedWeight = 1;
      }
    }
  }

  const isClickbait = clickbaitScore >= 2;

  // Generate cleaned title if clickbait
  if (isClickbait) {
    cleanedTitle = title
      .replace(/you won't believe/gi, '')
      .replace(/shocking[:]?\s*/gi, '')
      .replace(/!{2,}/g, '!')
      .replace(/\?{2,}/g, '?')
      .replace(/\.{3,}/g, '...')
      .replace(/^\d+\s+(things?|reasons?|ways?)\s+(why\s+)?/i, '')
      .replace(/here'?s why:?\s*/gi, '')
      .replace(/the truth about:?\s*/gi, '')
      .trim();

    // Capitalize first letter
    if (cleanedTitle.length > 0) {
      cleanedTitle = cleanedTitle.charAt(0).toUpperCase() + cleanedTitle.slice(1);
    }

    // If cleaned title is too short, use original
    if (cleanedTitle.length < 10) {
      cleanedTitle = title;
    }
  }

  // Generate explanation based on category
  const explanations: Record<ClickbaitCategory, string> = {
    'Curiosity Gap': 'Creates unanswered question to drive clicks',
    'Vague Drama': 'Uses dramatic language without specifics',
    'Info Withheld': 'Hides key information behind vague references',
    'Emotional': 'Uses emotional language to manipulate',
    'Superlatives': 'Exaggerates with extreme claims',
    'Fear Bait': 'Uses fear or outrage to drive engagement',
    'Teaser': 'Teases content without revealing substance',
    'Metaphor': 'Uses metaphors instead of facts',
  };

  return {
    isClickbait,
    cleanedTitle,
    category: isClickbait ? matchedCategory : undefined,
    explanation: isClickbait && matchedCategory ? explanations[matchedCategory] : undefined,
  };
};
